import type { Attachment, VoiceMeta } from '../fixtures/domain.js';
import { Icon } from '../components/Icon.js';
import { attachmentKind, formatFileSize, isPreviewable } from './attachment-rules.js';
import { useState } from 'react';

import { useChatFileLink } from './useChatFileLink.js';
import { VoicePlayer } from './VoicePlayer.js';
import styles from './AttachmentView.module.css';

/**
 * One file in a bubble: an image inline, a document as a card, and pressing either opens a tab.
 * On Stream's CDN `url` is public; a file in our own bucket gets short-lived links per view.
 */
export function AttachmentView({
  attachment,
  channelId,
}: {
  attachment: Attachment;
  channelId: string;
}) {
  if (attachment.voice) {
    return attachment.fileId ? (
      <BucketVoice voice={attachment.voice} channelId={channelId} fileId={attachment.fileId} />
    ) : (
      <VoicePlayer
        src={attachment.url}
        durationS={attachment.voice.durationS}
        waveform={attachment.voice.waveform}
      />
    );
  }
  return attachment.fileId ? (
    <BucketAttachment attachment={attachment} channelId={channelId} fileId={attachment.fileId} />
  ) : (
    <LinkedAttachment attachment={attachment} />
  );
}

/** A file in our own bucket: its links are short-lived and fetched for this view. */
function BucketAttachment({
  attachment,
  channelId,
  fileId,
}: {
  attachment: Attachment;
  channelId: string;
  fileId: string;
}) {
  // Pinned once it loads: each refresh is a new presigned url, and a new url is a new download.
  const [shown, setShown] = useState<string>();
  const awaitThumb = attachmentKind(attachment.name) === 'pdf';
  const { link, gone, retry } = useChatFileLink(channelId, fileId, awaitThumb);
  if (gone) return <FileStub name={attachment.name} note="No longer available" />;
  if (!link) return <FileStub name={attachment.name} note="Loading…" busy />;
  const latest = attachment.type === 'image' ? (link.thumbUrl ?? link.url) : link.thumbUrl;
  const src = shown ?? latest ?? undefined;
  return (
    <LinkedAttachment
      attachment={{ ...attachment, url: link.url, thumbUrl: src }}
      onImageLoad={() => setShown(src)}
      onImageError={() => {
        setShown(undefined);
        retry();
      }}
    />
  );
}

/** Follows each refreshed link until playback starts, then pins it so a refresh can't restart it. */
function BucketVoice({
  voice,
  channelId,
  fileId,
}: {
  voice: VoiceMeta;
  channelId: string;
  fileId: string;
}) {
  const [pinned, setPinned] = useState<string>();
  const { link, gone, retry } = useChatFileLink(channelId, fileId);
  if (gone) return <FileStub name="Voice note" note="No longer available" />;
  const src = pinned ?? link?.url;
  return (
    <VoicePlayer
      src={src}
      durationS={voice.durationS}
      waveform={voice.waveform}
      onPlay={() => setPinned(src)}
      onError={() => {
        setPinned(undefined);
        retry();
      }}
    />
  );
}

function LinkedAttachment({
  attachment,
  onImageLoad,
  onImageError,
}: {
  attachment: Attachment;
  onImageLoad?: () => void;
  onImageError?: () => void;
}) {
  const { type, url, name, sizeBytes, thumbUrl } = attachment;

  if (type === 'image') {
    // Opens in a new tab rather than a lightbox: the browser's own viewer beats a bespoke one.
    return (
      <a
        className={styles.image}
        href={url}
        target="_blank"
        rel="nofollow noreferrer noopener"
        title={name}
      >
        <img
          src={thumbUrl ?? url}
          alt={name}
          loading="lazy"
          onLoad={onImageLoad}
          onError={onImageError}
        />
      </a>
    );
  }

  const previewable = isPreviewable(attachmentKind(name));
  const size = formatFileSize(sizeBytes);

  /*
   * Every file is a card, and the window at its top is filled one of two ways.
   *
   * A document that arrived with a rendered first page shows it, which is the
   * point of the feature: for a thread whose whole job is deciding whether
   * somebody is worth talking to, "open this to see if it mattered" is the
   * wrong default — a portfolio you cannot see is a portfolio you have to take
   * on trust.
   *
   * Everything else shows what it is instead. That is the industry's answer for
   * a file nothing can render — a type mark, the name, the size — and giving it
   * the same frame as a real preview is what makes attachments read as one
   * component rather than as a feature that worked for some files and failed
   * for others.
   *
   * Only the picture is ever a render. Producing one for a Word document means
   * an office engine, which is a few hundred megabytes and a converter running
   * against a file a stranger uploaded, to replace a mark that already says
   * everything the name does.
   */
  return (
    <a
      className={styles.card}
      href={url}
      target="_blank"
      rel="nofollow noreferrer noopener"
      /*
       * Only where the browser cannot show it, and never load-bearing. Browsers
       * ignore `download` on a cross-origin link, and the file host is another origin,
       * so what actually happens is decided by the `Content-Disposition` it
       * serves. This is here to be correct if assets ever become same-origin.
       */
      {...(previewable ? {} : { download: name })}
      title={previewable ? `Open ${name}` : `Download ${name}`}
    >
      <span className={styles.preview}>
        {thumbUrl ? (
          <img
            className={styles.thumb}
            src={thumbUrl}
            alt=""
            loading="lazy"
            onLoad={onImageLoad}
            onError={onImageError}
          />
        ) : (
          <Placeholder name={name} />
        )}
      </span>
      <span className={styles.cardFoot}>
        <FileRow name={name} size={size} previewable={previewable} icon="fileText" />
      </span>
    </a>
  );
}

/** A file card with no link: still loading, or gone from the bucket. */
function FileStub({ name, note, busy }: { name: string; note: string; busy?: boolean }) {
  return (
    <span className={styles.card} aria-busy={busy || undefined}>
      <span className={styles.preview}>
        <Placeholder name={name} />
      </span>
      <span className={styles.cardFoot}>
        <FileRow name={name} size={null} previewable={false} icon="fileText" note={note} />
      </span>
    </span>
  );
}

/**
 * Decorative: the foot beneath already names the file in text, and a screen
 * reader reading the extension twice helps nobody.
 */
function Placeholder({ name }: { name: string }) {
  return (
    <span className={styles.placeholder} aria-hidden="true">
      <Icon name="fileText" size={38} />
      <span className={styles.placeholderKind}>{fileExtension(name)}</span>
    </span>
  );
}

/** The extension as a short mark — DOCX, PDF — or FILE when there is none. */
function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  const ext = dot === -1 ? '' : name.slice(dot + 1);
  return ext === '' || ext.length > 5 ? 'FILE' : ext.toUpperCase();
}

/**
 * The contents of a file row, without the link around them.
 *
 * Split out because the card foot carries the same line, and a second copy of it
 * is how the two drift apart.
 */
function FileRow({
  name,
  size,
  previewable,
  icon = 'paperclip',
  note,
}: {
  name: string;
  size: string | null;
  previewable: boolean;
  /** A card foot names the document; a bare row names the clip that holds it. */
  icon?: 'paperclip' | 'fileText';
  /** Said instead of Open/Download when there is nothing to open yet. */
  note?: string;
}) {
  return (
    <>
      <Icon name={icon} size={15} />
      <span className={styles.fileName}>{name}</span>
      {size && <span className={styles.fileMeta}>{size}</span>}
      {/*
       * The word, not only the glyph.
       *
       * With just an icon this row read as a download chip, and the question it
       * produced was "do I have to download this to see it?" — about a file that
       * opens in the browser's own viewer on one click. A 14px arrow is not a
       * promise anybody reads.
       */}
      {note ? (
        <span className={styles.fileAction}>{note}</span>
      ) : (
        <>
          <span className={styles.fileAction}>{previewable ? 'Open' : 'Download'}</span>
          <Icon name={previewable ? 'externalLink' : 'download'} size={14} />
        </>
      )}
    </>
  );
}
